import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Tooltip, Row, Col, Form } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { WorkloadComparison } from './WorkloadComparison';
import { WorkloadSectionProps, PairComparison } from './types';
import {
	StyledButtonTootipUnselected,
	StyledButtonTootipSelected,
	ComparisonContainer,
	StyledTooltipCol,
	TitleContainer,
	RightColumn,
	StyledTitle,
	LeftColumn,
	StyledText
} from './styles';

export function WorkloadSection({ workloadQuestions, saveAnswer }: WorkloadSectionProps): React.ReactElement | null {
	if (!workloadQuestions.length) return null;

	const pairComparisons: PairComparison[] = workloadQuestions
		.map((question) => {
			if (question.options && question.options.length >= 2) {
				return {
					questionId: question.id,
					option1: question.options[0],
					option2: question.options[1]
				};
			}
			return null;
		})
		.filter((item): item is PairComparison => item !== null);

	if (pairComparisons.length === 0) return null;

	const comparisonsPerColumn = Math.ceil(pairComparisons.length / 2);
	const leftColumnComparisons = pairComparisons.slice(0, comparisonsPerColumn);
	const rightColumnComparisons = pairComparisons.slice(comparisonsPerColumn);

	function renderComparisonColumn(comparisons: PairComparison[]): React.ReactNode {
		return comparisons.map((comparison) => (
			<Form.Item
				rules={[{ required: true, message: I18n.get('Please fill this field') }]}
				validateTrigger={['onChange', 'onBlur', 'onSubmit']}
				name={`question_${comparison.questionId}`}
				key={comparison.questionId}
				preserve={true}
			>
				<WorkloadComparison comparison={comparison} saveAnswer={saveAnswer} />
			</Form.Item>
		));
	}

	return (
		<>
			<TitleContainer>
				<StyledTitle level={4}>
					{I18n.get('Which of the following items influenced workload the most?')}&nbsp;
					<Tooltip
						placement="bottomLeft"
						showArrow={false}
						title={
							<Row>
								<Col span={24}>
									<p>
										{I18n.get(
											'Example where mental demand had a greater impact than physical demand:'
										)}
									</p>
								</Col>
								<StyledTooltipCol span={24}>
									<StyledButtonTootipSelected>
										{I18n.get('Mental demands')}
									</StyledButtonTootipSelected>
									<p style={{ textAlign: 'center', fontWeight: 600, marginTop: '10px' }}>
										{I18n.get('or')}
									</p>
									<StyledButtonTootipUnselected>
										{I18n.get('Physical demands')}
									</StyledButtonTootipUnselected>
								</StyledTooltipCol>
							</Row>
						}
						overlayInnerStyle={{
							boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
							backgroundColor: 'rgba(255, 255, 255, 1)',
							border: '1px solid rgba(0, 33, 133, 1)',
							borderRadius: '0 10px 10px 10px',
							color: 'rgba(38, 38, 38, 1)',
							fontWeight: 500,
							width: '300px'
						}}
					>
						<InfoCircleOutlined style={{ fontSize: '15px' }} />
					</Tooltip>
				</StyledTitle>
				<StyledText>{I18n.get('Select an alternative in each pair of options:')}</StyledText>
			</TitleContainer>

			<ComparisonContainer>
				<LeftColumn>{renderComparisonColumn(leftColumnComparisons)}</LeftColumn>
				<RightColumn>{renderComparisonColumn(rightColumnComparisons)}</RightColumn>
			</ComparisonContainer>
		</>
	);
}
